/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice } from "@reduxjs/toolkit";

import { ApiErrorCode, RequestStatus } from "~/constants";
import { resendVerifyAsync, signupAsync, verifyEmailAsync } from "~/features/signup/thunks";
import { SignupState } from "~/features/signup/types";
import { AsyncThunkError } from "~/state/types";

const initialState: SignupState = {
  signupStatus: RequestStatus.IDLE,
  signupEmail: null,
  verifyStatus: RequestStatus.IDLE,
  verifyMessage: null,
  resendVerifyStatus: RequestStatus.IDLE,
  resendVerifyEmail: null,
};

const slice = createSlice({
  name: "signup",
  initialState,
  reducers: {
    resetSignup: (state) => {
      state.signupStatus = RequestStatus.IDLE;
      state.signupEmail = null;
    },

    resetVerify: (state) => {
      state.verifyStatus = RequestStatus.IDLE;
      state.verifyMessage = "";
    },

    resetResendVerify: (state) => {
      state.resendVerifyStatus = RequestStatus.IDLE;
      state.resendVerifyEmail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Signup
      .addCase(signupAsync.pending, (state) => {
        state.signupStatus = RequestStatus.PENDING;
      })

      .addCase(signupAsync.fulfilled, (state, action) => {
        state.signupStatus = RequestStatus.SUCCESS;
        state.signupEmail = action.payload;
      })

      .addCase(signupAsync.rejected, (state) => {
        state.signupStatus = RequestStatus.ERROR;
      })

      // Verify
      .addCase(verifyEmailAsync.pending, (state) => {
        state.verifyStatus = RequestStatus.PENDING;
        state.verifyMessage = null;
      })

      .addCase(verifyEmailAsync.fulfilled, (state, action) => {
        state.verifyStatus = RequestStatus.SUCCESS;
        state.verifyMessage = action.payload;
      })

      .addCase(verifyEmailAsync.rejected, (state, action) => {
        state.verifyStatus = RequestStatus.ERROR;

        const {
          thunkError: { apiErrorCode },
        } = action.payload as AsyncThunkError;

        if (apiErrorCode) {
          const codeName = ApiErrorCode[apiErrorCode];
          state.verifyMessage = `verify.response.${codeName}`;
        }
      })

      // Signup
      .addCase(resendVerifyAsync.pending, (state) => {
        state.resendVerifyStatus = RequestStatus.PENDING;
      })

      .addCase(resendVerifyAsync.fulfilled, (state, action) => {
        state.resendVerifyStatus = RequestStatus.SUCCESS;
        state.resendVerifyEmail = action.payload;
      })

      .addCase(resendVerifyAsync.rejected, (state) => {
        state.resendVerifyStatus = RequestStatus.ERROR;
      });
  },
});

export const SignupActions = slice.actions;

export const signupReducer = slice.reducer;
